<template>
  <div class="modal-card">
    <CloseIcon :size="24" class="close-modal-icon close-on-click" />
    <div class="modal-card-header">
      <h1 class="title">Cancel Item: {{ ticketItem.productName }}</h1>
    </div>
    <div class="modal-card-body">
      <div class="form-grid">
        <div class="strategy-section">
          <div class="strategy-options">
            <div
              class="strategy-option"
              :class="{ 'selected': selectedStrategy === 'future' }"
              @click="selectedStrategy = 'future'"
            >
              <div class="radio-indicator">
                <div v-if="selectedStrategy === 'future'" class="radio-dot"></div>
              </div>
              <div class="strategy-content">
                <div class="strategy-title">Apply {{ formatUtils.formatCurrency(ticketItem.amountCents) }} credit to future receivables only</div>
                <div class="strategy-description" v-if="futureStrategyStats.count > 0">
                  {{ futureStrategyStats.count }} unpaid receivable{{ futureStrategyStats.count !== 1 ? 's' : '' }} will be reduced proportionally
                </div>
                <div class="strategy-description error" v-else>
                  ⚠️ No unpaid receivables available
                </div>
              </div>
            </div>

            <div
              class="strategy-option"
              :class="{ 'selected': selectedStrategy === 'all' }"
              @click="selectedStrategy = 'all'"
            >
              <div class="radio-indicator">
                <div v-if="selectedStrategy === 'all'" class="radio-dot"></div>
              </div>
              <div class="strategy-content">
                <div class="strategy-title">Apply {{ formatUtils.formatCurrency(ticketItem.amountCents) }} credit to all receivables</div>
                <div class="strategy-description">
                  {{ allStrategyStats.count }} receivable{{ allStrategyStats.count !== 1 ? 's' : '' }} will be reduced proportionally (some may become negative)
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="error" class="error-message">
          {{ error }}
        </div>
      </div>
    </div>
    <div class="modal-card-footer">
      <button class="button primary form-button" @click="handleSubmit" :disabled="isSubmitting || !canSubmit">
        {{ isSubmitting ? 'Processing...' : 'Cancel Item' }}
      </button>
    </div>
  </div>
</template>

<script>
import CloseIcon from 'vue-material-design-icons/Close.vue';
import * as formatUtils from '@/utils/formatters';
import * as api from '@/api';

export default {
  name: "CancelItemModal",
  props: {
    ticketItem: Object,
    ticket: Object,
    receivables: Array
  },
  emits: ['close', 'cancelled'],
  data() {
    return {
      formatUtils,
      selectedStrategy: 'future',
      isSubmitting: false,
      error: null
    }
  },
  computed: {
    allStrategyStats() {
      return { count: this.receivables.length };
    },
    futureStrategyStats() {
      const unpaidReceivables = this.receivables.filter(r => r.amountCentsPaid === 0);
      return { count: unpaidReceivables.length };
    },
    canSubmit() {
      if (this.selectedStrategy === 'future' && this.futureStrategyStats.count === 0) {
        return false;
      }
      return true;
    }
  },
  methods: {
    calculateNewReceivables() {
      const cancelAmount = this.ticketItem.amountCents;
      let receivablesToModify = [];

      if (this.selectedStrategy === 'all') {
        receivablesToModify = [...this.receivables];
      } else {
        receivablesToModify = this.receivables.filter(r => r.amountCentsPaid === 0);
      }

      if (receivablesToModify.length === 0) {
        throw new Error('No receivables available to modify');
      }

      const totalReceivableAmount = receivablesToModify.reduce((sum, r) => sum + r.amountCents, 0);

      if (totalReceivableAmount === 0) {
        throw new Error('Total receivable amount is zero, cannot distribute proportionally');
      }

      let distributedAmount = 0;
      const updatedReceivables = this.receivables.map((receivable, index, array) => {
        const shouldModify = receivablesToModify.some(r => r.receivableId === receivable.receivableId);

        if (shouldModify) {
          let reduction;

          const isLast = index === array.length - 1 ||
                        receivablesToModify.findIndex(r => r.receivableId === receivable.receivableId) === receivablesToModify.length - 1;

          if (isLast) {
            reduction = cancelAmount - distributedAmount;
          } else {
            const proportion = receivable.amountCents / totalReceivableAmount;
            reduction = Math.round(cancelAmount * proportion);
            distributedAmount += reduction;
          }

          const newAmount = receivable.amountCents - reduction;

          return {
            receivableId: receivable.receivableId,
            amountCents: newAmount
          };
        }

        return {
          receivableId: receivable.receivableId,
          amountCents: receivable.amountCents
        };
      });

      return updatedReceivables;
    },
    async handleSubmit() {
      this.error = null;

      if (!this.canSubmit) {
        this.error = 'Cannot cancel item with selected strategy';
        return;
      }

      this.isSubmitting = true;

      try {
        const newReceivables = this.calculateNewReceivables();

        const updatedTicketItems = this.ticket.ticketItems.map(item => {
          if (item.ticketItemId === this.ticketItem.ticketItemId) {
            return {
              ticketItemId: item.ticketItemId,
              quantity: item.quantity,
              durationMonths: item.durationMonths,
              amountCents: 0
            };
          }
          return {
            ticketItemId: item.ticketItemId,
            quantity: item.quantity,
            durationMonths: item.durationMonths,
            amountCents: item.amountCents
          };
        });

        const totalTicketAmount = updatedTicketItems.reduce((sum, item) => sum + item.amountCents, 0);
        const totalReceivableAmount = newReceivables.reduce((sum, r) => sum + r.amountCents, 0);

        if (totalTicketAmount !== totalReceivableAmount) {
          throw new Error(`Sum mismatch: ticket items = ${totalTicketAmount}, receivables = ${totalReceivableAmount}`);
        }

        const updateData = {
          patientId: this.ticket.patientId,
          counselorId: this.ticket.counselorId,
          dateTicketStart: this.ticket.dateTicketStart,
          ticketItems: updatedTicketItems,
          receivables: newReceivables
        };

        await api.updateTicket(this.ticket.ticketId, updateData);

        this.$emit('cancelled');

        const closeButton = this.$el.querySelector('.close-on-click');
        if (closeButton) {
          closeButton.click();
        }
      } catch (err) {
        this.error = err.message || 'Failed to cancel item';
      } finally {
        this.isSubmitting = false;
      }
    }
  },
  components: { CloseIcon }
};
</script>

<style scoped>
.modal-card {
  max-width: 600px;
}

.form-grid {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.strategy-section {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.strategy-options {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.strategy-option {
  display: flex;
  gap: 12px;
  padding: 16px;
  border: 2px solid #e5e7eb;
  border-radius: var(--border-radius);
  cursor: pointer;
  transition: all 0.2s;
  background-color: #fff;
}

.strategy-option:hover {
  border-color: #cbd5e1;
  background-color: #f9fafb;
}

.strategy-option.selected {
  border-color: var(--primary-color);
  background-color: #f0f4ff;
}

.radio-indicator {
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 2px solid #d1d5db;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  margin-top: 2px;
  transition: border-color 0.2s;
}

.strategy-option.selected .radio-indicator {
  border-color: var(--primary-color);
}

.radio-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background-color: var(--primary-color);
}

.strategy-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.strategy-title {
  font-weight: 600;
  color: #111827;
  font-size: 15px;
}

.strategy-description {
  color: #6b7280;
  font-size: 13px;
  line-height: 1.4;
}

.strategy-description.error {
  color: #991b1b;
}

.error-message {
  color: #d32f2f;
  font-size: 14px;
  padding: 8px 12px;
  background-color: #ffebee;
  border-radius: var(--border-radius);
}
</style>
